import { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { LeadStatus } from 'app/features/leads/services/gym-finder/types';
import ChevronDown from 'app/ui/components/atoms/icons/chevron-down';
import MoreVertical from 'app/ui/components/atoms/icons/more-vertical';

const BULK_STATUS_OPTIONS: readonly LeadStatus[] = ['new', 'contacted', 'attended'];

type LeadsBulkActionsProps = {
  selectedCount: number;
  isPending?: boolean;
  onSelectStatus: (status: LeadStatus) => void;
};

const Container = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme: { spacing } }) => spacing(11)};
  height: ${({ theme: { spacing } }) => spacing(11)};
  padding: 0;
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  border-radius: 50%;
  background: ${({ theme }) => theme.semantic.neutrals_200};
  color: ${({ theme }) => theme.semantic.text_small_elements};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        width: auto;
        height: auto;
        gap: ${spacing(2)};
        padding: ${spacing(2.5)} ${spacing(4)};
        border-radius: ${spacing(6)};
      `,
    )}
  `}
`;

const MobileTriggerContent = styled.span`
  display: inline-flex;
  align-items: center;

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        display: none;
      `,
    )}
  `}
`;

const DesktopTriggerContent = styled.span`
  display: none;

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        display: inline-flex;
        align-items: center;
        gap: ${spacing(2)};
      `,
    )}
  `}
`;

const TriggerLabel = styled.span`
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-weight: ${({ theme: { typography } }) => typography.light};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
`;

const ChevronWrapper = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-in-out;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme: { spacing } }) => spacing(1)});
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-width: 12.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  background: ${({ theme }) => theme.semantic.neutrals_200};
  overflow: hidden;
`;

const MenuItem = styled.button`
  cursor: pointer;
  border: none;
  background: transparent;
  text-align: left;
  padding: ${({ theme: { spacing } }) => `${spacing(2.5)} ${spacing(4)}`};
  color: ${({ theme }) => theme.semantic.text_small_elements};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-weight: ${({ theme: { typography } }) => typography.light};
  font-size: ${({ theme: { typography } }) => typography.xsmall};

  &:hover {
    background: ${({ theme }) => theme.semantic.neutrals_300};
  }

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: -${({ theme: { spacing } }) => spacing(0.5)};
  }
`;

export const LeadsBulkActions = ({ selectedCount, isPending = false, onSelectStatus }: LeadsBulkActionsProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (isOpen && focusIndex !== null) {
      itemRefs.current[focusIndex]?.focus();
    }
    if (!isOpen) {
      setFocusIndex(null);
    }
  }, [isOpen, focusIndex]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (status: LeadStatus) => {
    setIsOpen(false);
    triggerRef.current?.focus();
    onSelectStatus(status);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setFocusIndex(event.key === 'ArrowDown' ? 0 : BULK_STATUS_OPTIONS.length - 1);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setFocusIndex((current) => {
        const base = current ?? (direction === 1 ? -1 : 0);
        return (base + direction + BULK_STATUS_OPTIONS.length) % BULK_STATUS_OPTIONS.length;
      });
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setFocusIndex(event.key === 'Home' ? 0 : BULK_STATUS_OPTIONS.length - 1);
    }
  };

  const triggerLabel = intl.formatMessage({ id: 'leads.bulkActions.trigger' });

  return (
    <Container ref={containerRef} data-testid="leads-bulk-actions">
      <TriggerButton
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label={intl.formatMessage({ id: 'leads.bulkActions.triggerAriaLabel' }, { count: selectedCount })}
        data-testid="leads-bulk-actions-trigger"
        disabled={isPending}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
      >
        <MobileTriggerContent aria-hidden="true">
          <MoreVertical />
        </MobileTriggerContent>
        <DesktopTriggerContent>
          <ChevronWrapper $isOpen={isOpen}>
            <ChevronDown color={semantic.text_small_elements} />
          </ChevronWrapper>
          <TriggerLabel>{triggerLabel}</TriggerLabel>
        </DesktopTriggerContent>
      </TriggerButton>
      {isOpen ? (
        <Menu id={menuId} role="menu" aria-label={triggerLabel} onKeyDown={handleMenuKeyDown}>
          {BULK_STATUS_OPTIONS.map((status, index) => (
            <MenuItem
              key={status}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitem"
              tabIndex={focusIndex === index ? 0 : -1}
              data-testid={`leads-bulk-action-${status}`}
              onClick={() => handleSelect(status)}
              onMouseEnter={() => setFocusIndex(index)}
            >
              {intl.formatMessage({ id: `leads.bulkActions.markAs.${status}` })}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </Container>
  );
};

export default LeadsBulkActions;
